// ReceivablesPanel — embedded in the customer detail page. Lists the
// customer's outstanding (piutang) transactions, surfaces the due date
// / overdue state, and lets the cashier record a payment in two clicks.
// Also shows the global payment history for context.
//
// "Kirim reminder" is a one-tap wa.me deep link — no reminder system.

import { useMemo, useState } from 'react';
import {
  Wallet,
  CalendarClock,
  MessageCircle,
  AlertTriangle,
  CheckCircle2,
  X,
  Plus,
  Banknote,
  QrCode,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { cn, formatIDR, formatDate, formatRelative } from '@/lib/format';
import { useAuth } from '@/hooks/useAuth';
import {
  useOutstandingByCustomer,
  usePaymentsByCustomer,
  useRecordPayment,
} from '@/hooks/useReceivables';
import { derivePaymentStatus } from '@/types/domain';
import type {
  AppTransaction,
  PaymentMethod,
  ReceivablePayment,
} from '@/types/domain';

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  qris: 'QRIS',
  transfer: 'Transfer',
};

const METHOD_ICON: Record<PaymentMethod, typeof Banknote> = {
  cash: Banknote,
  qris: QrCode,
  transfer: Building2,
};

const QUICK_DENOMS = [50000, 100000, 200000, 500000];

export interface ReceivablesPanelProps {
  customerId: string;
  customerName: string;
  /** Phone for wa.me deep links. Sanitised at use-site. */
  customerPhone?: string | null;
}

export function ReceivablesPanel({
  customerId,
  customerName,
  customerPhone,
}: ReceivablesPanelProps) {
  const { data: outstanding, isLoading: loadingOutstanding } =
    useOutstandingByCustomer(customerId);
  const { data: payments, isLoading: loadingPayments } =
    usePaymentsByCustomer(customerId);

  const totalOutstanding = useMemo(
    () => (outstanding ?? []).reduce((s, t) => s + t.outstandingAmount, 0),
    [outstanding],
  );
  const overdueCount = useMemo(
    () =>
      (outstanding ?? []).filter(
        (t) => derivePaymentStatus(t) === 'overdue',
      ).length,
    [outstanding],
  );

  // Map transactionId -> most-recent payment for inline history hints.
  const lastPaymentByTx = useMemo(() => {
    const m = new Map<string, ReceivablePayment>();
    (payments ?? []).forEach((p) => {
      if (!m.has(p.transactionId)) m.set(p.transactionId, p);
    });
    return m;
  }, [payments]);

  // Modal state for "Catat Pembayaran"
  const [payingTx, setPayingTx] = useState<AppTransaction | null>(null);

  return (
    <Card padding="none" className="mb-6">
      <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-warning/10 text-warning flex items-center justify-center flex-shrink-0">
            <Wallet size={16} />
          </span>
          <div>
            <CardTitle>Piutang Aktif</CardTitle>
            <CardDescription>
              Tagihan yang belum lunas. Jatuh tempo lewat dari sekarang
              ditandai merah.
            </CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-overline uppercase text-ink-subtle font-medium">
              Total piutang
            </p>
            <p className="text-subhead font-display font-semibold text-ink tabular-nums">
              {formatIDR(totalOutstanding)}
            </p>
          </div>
          {overdueCount > 0 && (
            <Chip variant="danger">{overdueCount} overdue</Chip>
          )}
        </div>
      </div>

      {loadingOutstanding ? (
        <LoadingState message="Memuat piutang…" />
      ) : !outstanding || outstanding.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 size={24} strokeWidth={1.5} />}
          title="Tidak ada piutang aktif"
          description={`${customerName} tidak punya tagihan yang belum dibayar.`}
        />
      ) : (
        <div className="divide-y divide-line">
          {outstanding.map((tx) => (
            <ReceivableRow
              key={tx.id}
              tx={tx}
              lastPayment={lastPaymentByTx.get(tx.id)}
              customerName={customerName}
              customerPhone={customerPhone}
              onRecordPayment={() => setPayingTx(tx)}
            />
          ))}
        </div>
      )}

      {/* Payment history (compact) */}
      {!loadingPayments && payments && payments.length > 0 && (
        <div className="border-t border-line">
          <div className="px-5 py-3 flex items-center justify-between">
            <h4 className="text-small font-semibold text-ink">
              Riwayat Pembayaran
            </h4>
            <span className="text-caption text-ink-subtle">
              {payments.length} pembayaran
            </span>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Tanggal</TH>
                <TH>Invoice</TH>
                <TH>Method</TH>
                <TH>Catatan</TH>
                <TH className="text-right">Nominal</TH>
              </TR>
            </THead>
            <TBody>
              {payments.slice(0, 10).map((p) => (
                <TR key={p.id}>
                  <TD>
                    <span className="text-caption text-ink-subtle whitespace-nowrap">
                      {formatRelative(p.paidAt)}
                    </span>
                  </TD>
                  <TD>
                    <code className="text-caption font-mono text-ink">
                      {p.transactionId.slice(-6).toUpperCase()}
                    </code>
                  </TD>
                  <TD>
                    <span className="text-caption text-ink-subtle">
                      {METHOD_LABEL[p.paymentMethod]}
                    </span>
                  </TD>
                  <TD>
                    <span className="text-caption text-ink-subtle truncate inline-block max-w-[200px]">
                      {p.notes || '—'}
                    </span>
                  </TD>
                  <TD className="text-right">
                    <span className="font-medium text-success whitespace-nowrap tabular-nums">
                      +{formatIDR(p.amount)}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {payingTx && (
        <RecordPaymentModal
          tx={payingTx}
          onClose={() => setPayingTx(null)}
        />
      )}
    </Card>
  );
}

// ==== Row ====

function ReceivableRow({
  tx,
  lastPayment,
  customerName,
  customerPhone,
  onRecordPayment,
}: {
  tx: AppTransaction;
  lastPayment?: ReceivablePayment;
  customerName: string;
  customerPhone?: string | null;
  onRecordPayment: () => void;
}) {
  const status = derivePaymentStatus(tx);
  const isOverdue = status === 'overdue';
  const dueDate = tx.dueDate;

  const waLink = useMemo(() => {
    if (!customerPhone) return null;
    const phone = customerPhone.replace(/[^0-9]/g, '');
    if (!phone) return null;
    // Prepend 62 (Indonesia) if the number starts with 0.
    const normalized = phone.startsWith('0')
      ? `62${phone.slice(1)}`
      : phone.startsWith('62')
        ? phone
        : `62${phone}`;
    const dueText = dueDate ? format(dueDate, 'd MMM yyyy', { locale: idLocale }) : '—';
    const message =
      `Halo ${customerName},\n\n` +
      `Ini pengingat untuk invoice ${tx.invoiceNumber} ` +
      `senilai ${formatIDR(tx.outstandingAmount)} yang jatuh tempo ${dueText}. ` +
      `Mohon konfirmasi kalau sudah dibayar ya. Terima kasih! 🙏`;
    return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
  }, [customerPhone, customerName, tx.invoiceNumber, tx.outstandingAmount, dueDate]);

  return (
    <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-caption font-mono text-primary">
            {tx.invoiceNumber}
          </code>
          {isOverdue ? (
            <Chip variant="danger">
              <AlertTriangle size={11} className="mr-0.5" /> Overdue
            </Chip>
          ) : (
            <Chip variant="info">Tempo</Chip>
          )}
          {dueDate && (
            <span className="text-caption text-ink-subtle flex items-center gap-1">
              <CalendarClock size={11} /> Jatuh tempo {formatDate(dueDate)}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-caption text-ink-subtle">
          Total {formatIDR(tx.grandTotal)} · Terbayar{' '}
          {formatIDR(tx.paidAmount)}
          {lastPayment &&
            ` · Pembayaran terakhir ${formatRelative(lastPayment.paidAt)}`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right mr-2">
          <p className="text-overline uppercase text-ink-subtle font-medium">
            Sisa
          </p>
          <p
            className={cn(
              'text-body font-display font-semibold tabular-nums',
              isOverdue ? 'text-danger' : 'text-ink',
            )}
          >
            {formatIDR(tx.outstandingAmount)}
          </p>
        </div>
        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            aria-label="Kirim reminder via WhatsApp"
            title="Kirim reminder via WhatsApp"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-line text-ink-subtle hover:text-success hover:border-success/30 transition-colors"
          >
            <MessageCircle size={15} />
          </a>
        )}
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={onRecordPayment}
        >
          Catat bayar
        </Button>
      </div>
    </div>
  );
}

// ==== Record Payment Modal ====

function RecordPaymentModal({
  tx,
  onClose,
}: {
  tx: AppTransaction;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [amountInput, setAmountInput] = useState<string>(
    tx.outstandingAmount.toString(),
  );
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const recordPayment = useRecordPayment();

  const amount = useMemo(
    () => Number(amountInput.replace(/[^0-9]/g, '')) || 0,
    [amountInput],
  );

  const submit = async () => {
    setError(null);
    if (!user) return;
    if (amount <= 0) {
      setError('Nominal harus > 0');
      return;
    }
    if (amount > tx.outstandingAmount) {
      setError(
        `Nominal melebihi sisa piutang (${formatIDR(tx.outstandingAmount)})`,
      );
      return;
    }
    try {
      await recordPayment.mutateAsync({
        transactionId: tx.id,
        amount,
        paymentMethod: method,
        notes: notes.trim() || undefined,
        receivedBy: user.uid,
        receivedByName: user.displayName,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal catat pembayaran');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Catat Pembayaran Piutang"
      description={`${tx.invoiceNumber} · Sisa ${formatIDR(tx.outstandingAmount)}`}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <p className="text-small font-medium text-ink mb-2">Metode bayar</p>
          <div className="grid grid-cols-3 gap-2">
            {(['cash', 'qris', 'transfer'] as PaymentMethod[]).map((m) => {
              const Icon = METHOD_ICON[m];
              const active = method === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={cn(
                    'flex flex-col items-center gap-1 p-2.5 rounded border text-center',
                    active
                      ? 'border-primary bg-primary/5'
                      : 'border-line bg-surface hover:border-primary/30',
                  )}
                >
                  <Icon size={14} />
                  <span className="text-caption font-medium">
                    {METHOD_LABEL[m]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <Input
          label="Nominal dibayar"
          type="text"
          inputMode="numeric"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          error={error ?? undefined}
          leftIcon={<span className="text-caption">Rp</span>}
        />

        <div className="flex flex-wrap gap-1.5">
          {QUICK_DENOMS.filter((d) => d <= tx.outstandingAmount * 2).map(
            (d) => (
              <button
                key={d}
                type="button"
                onClick={() => setAmountInput(d.toString())}
                className="px-2.5 py-1 rounded-full text-caption bg-line/40 text-ink-subtle hover:bg-line/70"
              >
                {formatIDR(d)}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => setAmountInput(tx.outstandingAmount.toString())}
            className="px-2.5 py-1 rounded-full text-caption bg-primary/10 text-primary hover:bg-primary/20"
          >
            Lunas ({formatIDR(tx.outstandingAmount)})
          </button>
        </div>

        <Input
          label="Catatan (opsional)"
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Misal: bayar via BCA, cash di lokasi, dll"
        />

        <div className="p-3 rounded border border-line bg-canvas flex items-center justify-between text-caption">
          <span className="text-ink-subtle">Sisa setelah bayar</span>
          <span className="font-semibold text-ink tabular-nums">
            {formatIDR(Math.max(0, tx.outstandingAmount - amount))}
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          loading={recordPayment.isPending}
          disabled={amount <= 0 || amount > tx.outstandingAmount}
        >
          Catat pembayaran
        </Button>
      </div>
      {error && amount === 0 && (
        <p className="text-caption text-danger mt-1 flex items-center gap-1">
          <X size={11} /> {error}
        </p>
      )}
    </Modal>
  );
}
